import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getPendingApprovals } from '../api/endpoints';
import type { User } from '../types';

interface Props {
  user: User;
  onLogout: () => void;
  children: React.ReactNode;
}

export default function Layout({ user, onLogout, children }: Props) {
  const location = useLocation();
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    if (user.can_approve) {
      getPendingApprovals()
        .then((res) => setPendingCount(res.data.length))
        .catch(() => {});
    }
  }, [user.can_approve]);

  return (
    <div className="app-layout">
      <aside className="sidebar">
        <div className="sidebar-header">
          <h1>Constraint Manager</h1>
          <p>Dose Constraint System</p>
        </div>
        <nav className="sidebar-nav">
          <Link to="/" className={location.pathname === '/' ? 'active' : ''}>
            <span>Dashboard</span>
          </Link>
          <Link
            to="/approvals"
            className={location.pathname.startsWith('/approvals') ? 'active' : ''}
          >
            <span>Approvals</span>
            {pendingCount > 0 && <span className="nav-badge">{pendingCount}</span>}
          </Link>
          <Link
            to="/one-offs"
            className={location.pathname.startsWith('/one-offs') ? 'active' : ''}
          >
            <span>One-Offs</span>
          </Link>
          {user.reirradiation_enabled && (
            <Link
              to="/reirradiation"
              className={location.pathname.startsWith('/reirradiation') ? 'active' : ''}
            >
              <span>Reirradiation</span>
            </Link>
          )}
          <Link
            to="/raystation"
            className={location.pathname === '/raystation' ? 'active' : ''}
          >
            <span>RayStation</span>
          </Link>
          <Link
            to="/analytics"
            className={location.pathname === '/analytics' ? 'active' : ''}
          >
            <span>Analytics</span>
          </Link>
          <Link
            to="/consistency"
            className={location.pathname === '/consistency' ? 'active' : ''}
          >
            <span>Consistency Check</span>
          </Link>
          <Link
            to="/tools/bed-converter"
            className={location.pathname === '/tools/bed-converter' ? 'active' : ''}
          >
            <span>BED Calculator</span>
          </Link>
          <Link
            to="/audit"
            className={location.pathname === '/audit' ? 'active' : ''}
          >
            <span>Audit Log</span>
          </Link>
          {user.is_admin && (
            <Link
              to="/users"
              className={location.pathname === '/users' ? 'active' : ''}
            >
              <span>Users</span>
            </Link>
          )}
        </nav>
        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-name">{user.display_name}</div>
            <div className="user-role">{user.role}{user.is_admin ? ' · admin' : ''}</div>
          </div>
          <button className="btn-sm btn-outline" onClick={onLogout}>
            Log out
          </button>
        </div>
      </aside>
      <main className="main-content">{children}</main>
    </div>
  );
}
